import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Layers,
  Tag,
  BookOpen,
  Plus,
  Trash2,
  Edit2,
  Check,
  X,
  Archive,
  Search,
  History,
  Save,
  GripVertical,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  Image as ImageIcon,
  RefreshCw,
  Eye,
} from 'lucide-react';
import {
  listJourneyStages, createJourneyStage, updateJourneyStage, deleteJourneyStage,
  listCampaignTypes, createCampaignType, updateCampaignType, deleteCampaignType,
  listContents,      createContent,      updateContent,      deleteContent,
  uploadFlowNodesCsv,
  listCreatives,     uploadCreative,     updateCreative,     replaceCreative, archiveCreative, deleteCreative,
} from '../../utils/adminApi';

const TEAL = '#2EB4AC';
const TABS = [
  { key: 'stages',    label: 'Journey Stages',  icon: Layers },
  { key: 'types',     label: 'Campaign Types',  icon: Tag },
  { key: 'content',   label: 'Content Library', icon: BookOpen },
  { key: 'creatives', label: 'Creative Library',icon: ImageIcon },
  { key: 'upload',    label: 'Upload Center',   icon: Upload },
];

const AdminPanel = ({ showToast }) => {
  const [tab, setTab] = useState('stages');

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="admin-panel-page">
      <div className="flex gap-1 mb-5 border-b border-slate-200" data-testid="admin-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                active
                  ? 'border-teal-500 text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              style={active ? { borderColor: TEAL, color: '#0f172a' } : {}}
            >
              <Icon size={15} />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'stages'    && <StagesEditor    showToast={showToast} />}
      {tab === 'types'     && <TypesEditor     showToast={showToast} />}
      {tab === 'content'   && <ContentLibrary  showToast={showToast} />}
      {tab === 'creatives' && <CreativeLibrary showToast={showToast} />}
      {tab === 'upload'    && <UploadCenter    showToast={showToast} />}
    </div>
  );
};

// ---------- Journey Stages tab ----------
const StagesEditor = ({ showToast }) => {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ name: '', kind: 'campaign_bucket', order: '', description: '' });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setItems((await listJourneyStages()).data); }
    catch { showToast?.('Failed to load stages', 'error'); }
  };
  useEffect(() => { load(); }, []);  

  const add = async () => {
    if (!draft.name.trim()) return showToast?.('Stage name is required', 'error');
    setSaving(true);
    try {
      await createJourneyStage({ ...draft, order: draft.order || (items.length + 1) });
      setDraft({ name: '', kind: 'campaign_bucket', order: '', description: '' });
      showToast?.('Stage added', 'success');
      load();
    } catch (e) { showToast?.(e.response?.data?.detail || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await updateJourneyStage(editing.id, editing);
      setEditing(null);
      showToast?.('Stage updated', 'success');
      load();
    } catch (e) { showToast?.(e.response?.data?.detail || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this stage? Campaigns referencing it will be unassigned (not deleted).')) return;
    try { await deleteJourneyStage(id); showToast?.('Stage deleted', 'success'); load(); }
    catch (e) { showToast?.(e.response?.data?.detail || 'Failed', 'error'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-stages-section">
      {/* Add new */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-2">
        <input
          type="text"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder="Stage name (e.g. Wishlist)"
          className="flex-1 min-w-[180px] px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          data-testid="stage-name-input"
        />
        <select
          value={draft.kind}
          onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
          className="px-3 py-2 text-sm border border-slate-300 rounded-lg"
        >
          <option value="milestone">Milestone</option>
          <option value="campaign_bucket">Campaign Bucket</option>
        </select>
        <input
          type="number"
          value={draft.order}
          onChange={(e) => setDraft({ ...draft, order: e.target.value })}
          placeholder="Order"
          className="w-20 px-3 py-2 text-sm border border-slate-300 rounded-lg"
        />
        <input
          type="text"
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          placeholder="Short description"
          className="flex-1 min-w-[200px] px-3 py-2 text-sm border border-slate-300 rounded-lg"
        />
        <button
          onClick={add}
          disabled={saving}
          data-testid="stage-add-btn"
          className="flex items-center gap-1.5 px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 hover:opacity-90"
          style={{ backgroundColor: TEAL }}
        >
          <Plus size={14} />
          Add stage
        </button>
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left w-12">Order</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left w-40">Kind</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 w-28" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan="5" className="text-center text-xs text-slate-400 py-6">No stages yet.</td></tr>
            )}
            {items.map((s) => (
              <tr key={s.id} className="border-t border-slate-100">
                {editing?.id === s.id ? (
                  <>
                    <td className="px-3 py-2"><input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className="w-14 px-2 py-1 border border-slate-300 rounded" /></td>
                    <td className="px-3 py-2"><input type="text" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded" /></td>
                    <td className="px-3 py-2">
                      <select value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded">
                        <option value="milestone">Milestone</option>
                        <option value="campaign_bucket">Campaign Bucket</option>
                      </select>
                    </td>
                    <td className="px-3 py-2"><input type="text" value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded" /></td>
                    <td className="px-3 py-2 flex gap-1 justify-end">
                      <button onClick={saveEdit} disabled={saving} className="p-1.5 text-white rounded" style={{ backgroundColor: TEAL }}><Check size={14} /></button>
                      <button onClick={() => setEditing(null)} className="p-1.5 bg-slate-200 rounded"><X size={14} /></button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-3 py-2 text-slate-500">{s.order}</td>
                    <td className="px-3 py-2 font-semibold text-slate-900">{s.name}</td>
                    <td className="px-3 py-2"><span className={`text-[10px] px-2 py-0.5 rounded-full ${s.kind === 'milestone' ? 'bg-slate-100 text-slate-600' : 'bg-teal-50 text-teal-700'}`}>{s.kind}</span></td>
                    <td className="px-3 py-2 text-slate-500 text-xs">{s.description}</td>
                    <td className="px-3 py-2 flex gap-1 justify-end">
                      <button onClick={() => setEditing(s)} className="p-1.5 hover:bg-slate-100 rounded text-slate-500"><Edit2 size={14} /></button>
                      <button onClick={() => remove(s.id)} data-testid={`stage-delete-${s.id}`} className="p-1.5 hover:bg-rose-50 rounded text-rose-500"><Trash2 size={14} /></button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- Campaign Types tab ----------
const TypesEditor = ({ showToast }) => {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ name: '', color: '#2EB4AC', description: '' });
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { setItems((await listCampaignTypes()).data); }
    catch { showToast?.('Failed to load types', 'error'); }
  };
  useEffect(() => { load(); }, []);  

  const add = async () => {
    if (!draft.name.trim()) return showToast?.('Type name is required', 'error');
    try {
      await createCampaignType(draft);
      setDraft({ name: '', color: '#2EB4AC', description: '' });
      showToast?.('Type added', 'success');
      load();
    } catch (e) { showToast?.(e.response?.data?.detail || 'Failed', 'error'); }
  };

  const saveEdit = async () => {
    try {
      await updateCampaignType(editing.id, editing);
      setEditing(null);
      showToast?.('Type updated', 'success');
      load();
    } catch (e) { showToast?.(e.response?.data?.detail || 'Failed', 'error'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this type? Campaigns will be unassigned.')) return;
    try { await deleteCampaignType(id); showToast?.('Type deleted', 'success'); load(); }
    catch (e) { showToast?.(e.response?.data?.detail || 'Failed', 'error'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-types-section">
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-2">
        <input
          type="text"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder="Type name (e.g. UPI Recovery)"
          className="flex-1 min-w-[180px] px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          data-testid="type-name-input"
        />
        <input
          type="color"
          value={draft.color}
          onChange={(e) => setDraft({ ...draft, color: e.target.value })}
          className="h-9 w-12 rounded border border-slate-300 cursor-pointer"
        />
        <input
          type="text"
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          placeholder="Short description"
          className="flex-1 min-w-[200px] px-3 py-2 text-sm border border-slate-300 rounded-lg"
        />
        <button
          onClick={add}
          data-testid="type-add-btn"
          className="flex items-center gap-1.5 px-4 py-2 text-sm text-white rounded-lg hover:opacity-90"
          style={{ backgroundColor: TEAL }}
        >
          <Plus size={14} /> Add type
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((t) => (
          <div key={t.id} className="bg-white border border-slate-200 rounded-xl p-3">
            {editing?.id === t.id ? (
              <div className="space-y-2">
                <input type="text" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded" />
                <div className="flex items-center gap-2">
                  <input type="color" value={editing.color} onChange={(e) => setEditing({ ...editing, color: e.target.value })} className="h-7 w-10 rounded border border-slate-300" />
                  <input type="text" value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Description" className="flex-1 px-2 py-1.5 text-sm border border-slate-300 rounded" />
                </div>
                <div className="flex gap-1 justify-end">
                  <button onClick={saveEdit} className="p-1.5 text-white rounded" style={{ backgroundColor: TEAL }}><Check size={14} /></button>
                  <button onClick={() => setEditing(null)} className="p-1.5 bg-slate-200 rounded"><X size={14} /></button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="inline-block w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: t.color }} />
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{t.name}</div>
                    {t.description && <div className="text-[11px] text-slate-500 truncate">{t.description}</div>}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => setEditing(t)} className="p-1.5 hover:bg-slate-100 rounded text-slate-500"><Edit2 size={13} /></button>
                  <button onClick={() => remove(t.id)} className="p-1.5 hover:bg-rose-50 rounded text-rose-500"><Trash2 size={13} /></button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Content Library tab ----------
const ContentLibrary = ({ showToast }) => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState({ name: '', body: '', tags: '' });
  const [editing, setEditing] = useState(null);
  const [historyOf, setHistoryOf] = useState(null);

  const load = async () => {
    try {
      const params = {};
      if (includeArchived) params.include_archived = true;
      if (search) params.search = search;
      setItems((await listContents(params)).data);
    } catch { showToast?.('Failed to load contents', 'error'); }
  };
  useEffect(() => { load(); }, [includeArchived, search]);  

  const filteredTags = useMemo(() => {
    const s = new Set();
    items.forEach((c) => (c.tags || []).forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [items]);

  const save = async () => {
    try {
      await createContent({ name: draft.name, body: draft.body, tags: draft.tags });
      setDraft({ name: '', body: '', tags: '' });
      setShowCreate(false);
      showToast?.('Content created', 'success');
      load();
    } catch (e) { showToast?.(e.response?.data?.detail || 'Failed', 'error'); }
  };

  const saveEdit = async () => {
    try {
      await updateContent(editing.id, {
        name: editing.name, body: editing.body,
        tags: Array.isArray(editing.tags) ? editing.tags.join(',') : editing.tags,
      });
      setEditing(null);
      showToast?.('Content updated', 'success');
      load();
    } catch (e) { showToast?.(e.response?.data?.detail || 'Failed', 'error'); }
  };

  const toggleArchive = async (item) => {
    try {
      await updateContent(item.id, { status: item.status === 'archived' ? 'active' : 'archived' });
      load();
    } catch (e) { showToast?.('Failed', 'error'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Permanently delete this content?')) return;
    try { await deleteContent(id); load(); }
    catch (e) { showToast?.('Failed', 'error'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-content-section">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search content by name or body..."
            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            data-testid="content-search"
          />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />
          Show archived
        </label>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="content-add-btn"
          className="flex items-center gap-1.5 px-4 py-2 text-sm text-white rounded-lg hover:opacity-90"
          style={{ backgroundColor: TEAL }}
        >
          <Plus size={14} /> New content
        </button>
      </div>

      {filteredTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {filteredTags.map((tag) => (
            <button
              key={tag}
              onClick={() => setSearch(tag)}
              className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
            >
              #{tag}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.length === 0 && (
          <div className="col-span-2 text-center text-sm text-slate-400 py-12">No content yet.</div>
        )}
        {items.map((c) => (
          <div key={c.id} className={`bg-white border rounded-xl p-4 ${c.status === 'archived' ? 'border-dashed border-slate-300 opacity-70' : 'border-slate-200'}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-slate-900 truncate">{c.name}</h3>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 flex-shrink-0">v{c.version}</span>
                  {c.status === 'archived' && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 flex-shrink-0">Archived</span>}
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(c.tags || []).map((t) => <span key={t} className="text-[10px] px-1.5 py-0.5 bg-teal-50 text-teal-700 rounded-full">#{t}</span>)}
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => setEditing(c)} className="p-1.5 hover:bg-slate-100 rounded text-slate-500"><Edit2 size={13} /></button>
                <button onClick={() => setHistoryOf(c)} className="p-1.5 hover:bg-slate-100 rounded text-slate-500" title="Version history"><History size={13} /></button>
                <button onClick={() => toggleArchive(c)} className="p-1.5 hover:bg-amber-50 rounded text-amber-600"><Archive size={13} /></button>
                <button onClick={() => remove(c.id)} className="p-1.5 hover:bg-rose-50 rounded text-rose-500"><Trash2 size={13} /></button>
              </div>
            </div>
            <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans line-clamp-4">{c.body}</pre>
          </div>
        ))}
      </div>

      {/* Create modal */}
      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="New campaign content">
          <Input label="Name" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} placeholder="e.g. Cart reminder v1" />
          <Input label="Tags (comma-separated)" value={draft.tags} onChange={(v) => setDraft({ ...draft, tags: v })} placeholder="cart, wellcore" />
          <Textarea label="Body" value={draft.body} onChange={(v) => setDraft({ ...draft, body: v })} placeholder="Hi {name}, your cart..." rows={6} />
          <ModalFooter onCancel={() => setShowCreate(false)} onSave={save} disabled={!draft.name} />
        </Modal>
      )}

      {/* Edit modal */}
      {editing && (
        <Modal onClose={() => setEditing(null)} title="Edit content">
          <Input label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
          <Input label="Tags" value={Array.isArray(editing.tags) ? editing.tags.join(', ') : editing.tags} onChange={(v) => setEditing({ ...editing, tags: v })} />
          <Textarea label="Body" value={editing.body} onChange={(v) => setEditing({ ...editing, body: v })} rows={6} />
          <p className="text-[10px] text-slate-400 mt-1">Saving with a different body creates a new version (currently v{editing.version}).</p>
          <ModalFooter onCancel={() => setEditing(null)} onSave={saveEdit} />
        </Modal>
      )}

      {/* History drawer */}
      {historyOf && (
        <Modal onClose={() => setHistoryOf(null)} title={`Version history — ${historyOf.name}`}>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {(historyOf.versions || []).slice().reverse().map((v) => (
              <div key={v.version} className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-slate-900">Version {v.version}</span>
                  <span className="text-[10px] text-slate-400">{new Date(v.edited_at).toLocaleString()}</span>
                </div>
                <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans">{v.body}</pre>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
};

// ---------- Tiny reusable bits ----------
const Modal = ({ children, title, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 text-slate-500"><X size={16} /></button>
      </div>
      <div className="p-5 space-y-3">{children}</div>
    </div>
  </div>
);

const Input = ({ label, value, onChange, placeholder, type = 'text', testid }) => (
  <div>
    <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
  </div>
);

// Alias for code that uses ModalInput naming convention
const ModalInput = Input;

const Textarea = ({ label, value, onChange, rows = 4, placeholder }) => (
  <div>
    <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">{label}</label>
    <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows} placeholder={placeholder} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 font-mono" />
  </div>
);

const ModalFooter = ({ onCancel, onSave, disabled }) => (
  <div className="flex justify-end gap-2 pt-2">
    <button onClick={onCancel} className="px-4 py-2 text-sm bg-slate-200 rounded-lg">Cancel</button>
    <button onClick={onSave} disabled={disabled} className="flex items-center gap-1.5 px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 hover:opacity-90" style={{ backgroundColor: TEAL }}>
      <Save size={13} /> Save
    </button>
  </div>
);

// =====================  Creative Library  =====================
const API_BASE = process.env.REACT_APP_BACKEND_URL;
const ALLOWED_CREATIVE_EXTS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'];

const CreativeLibrary = ({ showToast }) => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // existing creative
  const [adding, setAdding] = useState(false);
  const [previewing, setPreviewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listCreatives({ include_archived: showArchived, search: search || undefined });
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      showToast?.(e.response?.data?.detail || 'Failed to load creatives', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load();   }, [showArchived]);

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const onArchiveToggle = async (item) => {
    try {
      await archiveCreative(item.id, item.status !== 'archived');
      showToast?.(item.status !== 'archived' ? 'Creative archived' : 'Creative restored', 'success');
      load();
    } catch (e) { showToast?.(e.response?.data?.detail || 'Failed', 'error'); }
  };

  const onDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}" permanently? This cannot be undone.`)) return;
    try {
      await deleteCreative(item.id);
      showToast?.('Creative deleted', 'success');
      load();
    } catch (e) { showToast?.(e.response?.data?.detail || 'Failed', 'error'); }
  };

  return (
    <div className="space-y-4" data-testid="creative-library">
      <div className="flex flex-wrap items-center gap-2">
        <form onSubmit={onSearch} className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, tag, or note..."
            data-testid="creative-search-input"
            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </form>
        <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer select-none">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)}
            data-testid="creative-show-archived" className="rounded" />
          Show archived
        </label>
        <button onClick={() => setAdding(true)} data-testid="creative-add-btn"
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white rounded-lg hover:opacity-90"
          style={{ backgroundColor: TEAL }}>
          <Plus size={13} /> Upload creative
        </button>
      </div>

      {loading ? (
        <p className="text-xs text-slate-400 text-center py-6">Loading creatives...</p>
      ) : items.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl">
          <ImageIcon size={32} className="mx-auto mb-2 text-slate-300" />
          <p className="text-sm font-semibold text-slate-700">No creatives yet</p>
          <p className="text-[11px] text-slate-500 mt-1">Upload your first image creative to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="creative-grid">
          {items.map((c) => (
            <CreativeCard
              key={c.id}
              creative={c}
              onPreview={() => setPreviewing(c)}
              onEdit={() => setEditing(c)}
              onArchive={() => onArchiveToggle(c)}
              onDelete={() => onDelete(c)}
            />
          ))}
        </div>
      )}

      {adding   && <CreativeUploadModal onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} showToast={showToast} />}
      {editing  && <CreativeEditModal   creative={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} showToast={showToast} />}
      {previewing && <CreativePreviewModal creative={previewing} onClose={() => setPreviewing(null)} />}
    </div>
  );
};

const CreativeCard = ({ creative, onPreview, onEdit, onArchive, onDelete }) => {
  const isArchived = creative.status === 'archived';
  return (
    <div
      className={`bg-white border rounded-xl overflow-hidden flex flex-col group ${isArchived ? 'border-slate-200 opacity-70' : 'border-slate-200 hover:border-teal-300 hover:shadow-md'} transition-all`}
      data-testid={`creative-card-${creative.name}`}
    >
      <button onClick={onPreview} className="aspect-square bg-slate-50 flex items-center justify-center relative overflow-hidden"
        data-testid={`creative-preview-${creative.name}`}>
        <img src={`${API_BASE}${creative.url}`} alt={creative.name}
          className="w-full h-full object-contain p-2"
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }} />
        <div className="hidden absolute inset-0 items-center justify-center flex-col gap-1 text-slate-400">
          <ImageIcon size={32} />
          <span className="text-[10px]">Preview unavailable</span>
        </div>
        {isArchived && (
          <span className="absolute top-2 left-2 text-[9px] font-bold uppercase tracking-wider bg-slate-700 text-white px-1.5 py-0.5 rounded">archived</span>
        )}
        <span className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-wider bg-white/90 text-slate-700 px-1.5 py-0.5 rounded">{creative.ext}</span>
      </button>
      <div className="p-2.5 flex-1 flex flex-col">
        <div className="text-[11px] font-bold text-slate-900 truncate" title={creative.name}>{creative.name}</div>
        <div className="text-[9px] text-slate-400 mt-0.5">
          {new Date(creative.created_at).toLocaleDateString()} · {(creative.size_bytes / 1024).toFixed(1)} KB
        </div>
        {(creative.tags || []).length > 0 && (
          <div className="flex flex-wrap gap-0.5 mt-1.5">
            {creative.tags.slice(0, 3).map((t) => (
              <span key={t} className="text-[9px] px-1.5 py-0.5 bg-teal-50 text-teal-700 rounded-full">#{t}</span>
            ))}
            {creative.tags.length > 3 && <span className="text-[9px] text-slate-400">+{creative.tags.length - 3}</span>}
          </div>
        )}
        <div className="flex items-center gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <IconBtn icon={Eye}     onClick={onPreview} label="Preview" testid={`creative-action-preview-${creative.name}`} />
          <IconBtn icon={Edit2}   onClick={onEdit}    label="Edit"    testid={`creative-action-edit-${creative.name}`} />
          <IconBtn icon={Archive} onClick={onArchive} label={isArchived ? 'Restore' : 'Archive'} testid={`creative-action-archive-${creative.name}`} />
          <IconBtn icon={Trash2}  onClick={onDelete}  label="Delete"  danger testid={`creative-action-delete-${creative.name}`} />
        </div>
      </div>
    </div>
  );
};

const IconBtn = ({ icon: Icon, onClick, label, danger, testid }) => (
  <button onClick={onClick} title={label} data-testid={testid}
    className={`p-1 rounded hover:bg-slate-100 ${danger ? 'text-rose-500 hover:bg-rose-50' : 'text-slate-500'}`}>
    <Icon size={12} />
  </button>
);

const CreativeUploadModal = ({ onClose, onSaved, showToast }) => {
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [tags, setTags] = useState('');
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_CREATIVE_EXTS.includes(ext)) {
      showToast?.(`Unsupported file. Allowed: ${ALLOWED_CREATIVE_EXTS.join(', ').toUpperCase()}`, 'error');
      return;
    }
    setFile(f);
    if (!name) setName(f.name.replace(/\.[^.]+$/, ''));
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(f));
  };

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const onSubmit = async () => {
    if (!file) { showToast?.('Pick a file first', 'error'); return; }
    setUploading(true);
    try {
      await uploadCreative(file, { name: name.trim(), tags, notes });
      showToast?.('Creative uploaded', 'success');
      onSaved();
    } catch (e) {
      showToast?.(e.response?.data?.detail || 'Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="creative-upload-modal">
      <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">Upload creative</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 text-slate-500"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block cursor-pointer">
            <input type="file" accept={ALLOWED_CREATIVE_EXTS.map((e) => `.${e}`).join(',')} onChange={onPick}
              data-testid="creative-upload-file" className="hidden" />
            <div className="border-2 border-dashed border-slate-300 hover:border-teal-400 hover:bg-teal-50 rounded-lg p-4 text-center transition-colors">
              {previewUrl ? (
                <div className="space-y-2">
                  <img src={previewUrl} alt="preview" className="max-h-40 mx-auto rounded-md" />
                  <div className="text-[11px] text-slate-700">{file?.name} · {(file?.size / 1024).toFixed(1)} KB</div>
                  <div className="text-[10px] text-teal-700 font-semibold">Click to change file</div>
                </div>
              ) : (
                <>
                  <Upload size={20} className="mx-auto mb-1 text-slate-400" />
                  <div className="text-xs font-semibold text-slate-700">Click to choose file</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{ALLOWED_CREATIVE_EXTS.join(', ').toUpperCase()} · any size</div>
                </>
              )}
            </div>
          </label>
          <ModalInput label="Name" value={name} onChange={setName} placeholder="e.g. Wellcore PDP Hero v1" testid="creative-upload-name" />
          <ModalInput label="Tags (comma-separated)" value={tags} onChange={setTags} placeholder="e.g. wellcore, pdp, hero" testid="creative-upload-tags" />
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder="Optional context, source, briefing link..."
              data-testid="creative-upload-notes"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm bg-slate-200 rounded-lg">Cancel</button>
            <button onClick={onSubmit} disabled={!file || uploading}
              data-testid="creative-upload-submit"
              className="px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 hover:opacity-90"
              style={{ backgroundColor: TEAL }}>
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CreativeEditModal = ({ creative, onClose, onSaved, showToast }) => {
  const [name, setName] = useState(creative.name || '');
  const [tags, setTags] = useState((creative.tags || []).join(', '));
  const [notes, setNotes] = useState(creative.notes || '');
  const [replacing, setReplacing] = useState(false);
  const [saving, setSaving] = useState(false);

  const onSave = async () => {
    setSaving(true);
    try {
      await updateCreative(creative.id, { name, tags, notes });
      showToast?.('Creative updated', 'success');
      onSaved();
    } catch (e) {
      showToast?.(e.response?.data?.detail || 'Save failed', 'error');
    } finally { setSaving(false); }
  };

  const onReplaceFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_CREATIVE_EXTS.includes(ext)) {
      showToast?.(`Unsupported file. Allowed: ${ALLOWED_CREATIVE_EXTS.join(', ').toUpperCase()}`, 'error');
      return;
    }
    setReplacing(true);
    try {
      await replaceCreative(creative.id, f);
      showToast?.('File replaced — version bumped', 'success');
      onSaved();
    } catch (err) {
      showToast?.(err.response?.data?.detail || 'Replace failed', 'error');
    } finally { setReplacing(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="creative-edit-modal">
      <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">Edit creative <span className="text-[10px] text-slate-400 ml-1">v{creative.version}</span></h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 text-slate-500"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="aspect-video bg-slate-50 rounded-lg overflow-hidden flex items-center justify-center">
            <img src={`${API_BASE}${creative.url}`} alt={creative.name} className="max-h-full max-w-full object-contain" />
          </div>
          <label className="block">
            <input type="file" accept={ALLOWED_CREATIVE_EXTS.map((e) => `.${e}`).join(',')} onChange={onReplaceFile} data-testid="creative-edit-replace-file" className="hidden" />
            <div className="border border-slate-200 rounded-lg p-2 text-center text-[11px] text-teal-700 font-semibold cursor-pointer hover:bg-teal-50 flex items-center justify-center gap-1.5">
              <RefreshCw size={11} /> {replacing ? 'Replacing...' : 'Replace file (keeps id, bumps version)'}
            </div>
          </label>
          <ModalInput label="Name *" value={name} onChange={setName} testid="creative-edit-name" />
          <ModalInput label="Tags (comma-separated)" value={tags} onChange={setTags} testid="creative-edit-tags" />
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              data-testid="creative-edit-notes"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm bg-slate-200 rounded-lg">Cancel</button>
            <button onClick={onSave} disabled={saving || !name.trim()}
              data-testid="creative-edit-save"
              className="px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 hover:opacity-90"
              style={{ backgroundColor: TEAL }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CreativePreviewModal = ({ creative, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}
    data-testid="creative-preview-modal">
    <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white">
      <X size={20} />
    </button>
    <div className="max-w-[90vw] max-h-[90vh] flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
      <img src={`${API_BASE}${creative.url}`} alt={creative.name} className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl" />
      <div className="text-white text-center">
        <div className="text-sm font-semibold">{creative.name}</div>
        <div className="text-[11px] opacity-70 mt-0.5">
          {creative.ext.toUpperCase()} · {(creative.size_bytes / 1024).toFixed(1)} KB · uploaded {new Date(creative.created_at).toLocaleString()}
        </div>
      </div>
    </div>
  </div>
);

// =====================  Upload Center  =====================
const UploadCenter = ({ showToast }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState('update');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const readFile = (f) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const csv = e.target.result;
      const firstLines = csv.split('\n').slice(0, 6).join('\n');
      setPreview(firstLines);
    };
    reader.readAsText(f);
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    readFile(f);
  };

  const onUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const reader = new FileReader();
      const text = await new Promise((resolve, reject) => {
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });
      const res = await uploadFlowNodesCsv(text, mode);
      setResult(res.data);
      showToast?.(`Done — ${res.data.created} created, ${res.data.updated} updated, ${res.data.skipped} skipped`, 'success');
    } catch (e) {
      showToast?.(e.response?.data?.detail || 'Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  const downloadSample = () => {
    const sample = `name,brand,stage,type,status,month,revenue,spend,sent,delivered,seen,clicks
Shilajit PDP Dropout,Shilajit,PDP Campaigns,PDP Campaign,active,Jan,120000,18000,42000,40000,28000,3800
Shilajit PDP Dropout,Shilajit,PDP Campaigns,PDP Campaign,active,Feb,135000,19000,44000,42000,29000,4000
Shilajit PDP Dropout,Shilajit,PDP Campaigns,PDP Campaign,active,Mar,141000,20000,45000,43000,30000,4100
`;
    const blob = new Blob([sample], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'flow-nodes-sample.csv';
    a.click();
  };

  return (
    <div className="space-y-5" data-testid="upload-center">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-start gap-3 mb-4">
          <FileSpreadsheet size={20} style={{ color: TEAL }} />
          <div>
            <h3 className="text-sm font-bold text-slate-900">Bulk upload campaigns & metrics</h3>
            <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
              Upload a CSV to create or update flow nodes in bulk. Supported columns: <code className="bg-slate-100 px-1 rounded">name</code>, <code className="bg-slate-100 px-1 rounded">brand</code>, <code className="bg-slate-100 px-1 rounded">stage</code>, <code className="bg-slate-100 px-1 rounded">type</code>, <code className="bg-slate-100 px-1 rounded">status</code>, <code className="bg-slate-100 px-1 rounded">month</code>, <code className="bg-slate-100 px-1 rounded">revenue</code>, <code className="bg-slate-100 px-1 rounded">spend</code>, <code className="bg-slate-100 px-1 rounded">sent</code>, <code className="bg-slate-100 px-1 rounded">delivered</code>, <code className="bg-slate-100 px-1 rounded">seen</code>, <code className="bg-slate-100 px-1 rounded">clicks</code>. One row per (name × month).
            </p>
            <button onClick={downloadSample} className="text-[11px] text-teal-700 font-semibold hover:underline mt-1.5" data-testid="upload-sample-download">
              Download sample CSV →
            </button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-stretch gap-3">
          <label className="flex-1 cursor-pointer">
            <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onPick} data-testid="upload-file-input" />
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 hover:border-teal-400 hover:bg-teal-50 transition-colors text-center">
              <Upload size={18} className="mx-auto mb-1 text-slate-400" />
              <div className="text-xs font-semibold text-slate-700">{file ? file.name : 'Click to choose CSV file'}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">{file ? `${(file.size / 1024).toFixed(1)} KB` : 'or drag & drop'}</div>
            </div>
          </label>
          <div className="md:w-56 space-y-2">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">If campaign exists</label>
              <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="upload-mode-select"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500">
                <option value="update">Update existing</option>
                <option value="duplicate">Create duplicate</option>
                <option value="skip">Skip</option>
              </select>
            </div>
            <button onClick={onUpload} disabled={!file || uploading} data-testid="upload-submit-btn"
              className="w-full px-4 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-50 hover:opacity-90"
              style={{ backgroundColor: TEAL }}>
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </div>

        {preview && (
          <div className="mt-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Preview (first 5 rows)</div>
            <pre className="text-[10px] bg-slate-50 border border-slate-200 rounded-md p-2 overflow-x-auto whitespace-pre">{preview}</pre>
          </div>
        )}
      </div>

      {result && (
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="upload-result">
          <div className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
            <Check size={14} className="text-emerald-500" /> Upload complete
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <ResultStat label="Created"    value={result.created    || 0} accent="text-emerald-700 bg-emerald-50 border-emerald-200" />
            <ResultStat label="Updated"    value={result.updated    || 0} accent="text-teal-700 bg-teal-50 border-teal-200" />
            <ResultStat label="Duplicated" value={result.duplicated || 0} accent="text-violet-700 bg-violet-50 border-violet-200" />
            <ResultStat label="Skipped"    value={result.skipped    || 0} accent="text-slate-700 bg-slate-50 border-slate-200" />
          </div>
          {(result.errors || []).length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-700 mb-1">
                <AlertCircle size={12} /> {result.errors.length} row warning(s)
              </div>
              <div className="text-[10px] bg-amber-50 border border-amber-200 rounded-md p-2 max-h-32 overflow-y-auto space-y-0.5">
                {result.errors.slice(0, 50).map((e, i) => (
                  <div key={i}><strong>Line {e.line}:</strong> {e.error}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ResultStat = ({ label, value, accent }) => (
  <div className={`rounded-lg border px-3 py-2 ${accent}`}>
    <div className="text-[10px] font-semibold uppercase tracking-wider opacity-70">{label}</div>
    <div className="text-xl font-bold mt-0.5 tabular-nums">{value}</div>
  </div>
);

export default AdminPanel;
